import { useState, useEffect } from 'react'
import { DollarSign, AlertCircle, Calculator } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'
import { financeV2Service } from '../services/financeV2Service'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'

import { getErrorMessage } from '../utils/error.utils'
interface CollectContingencyFeeModalProps {
  isOpen: boolean
  onClose: () => void
  fee: {
    id: string
    percentage: number
    description: string
    case_title?: string
  }
  onSuccess: () => void
}

interface Account {
  id: string
  name: string
  holderName: string
}

export function CollectContingencyFeeModal({
  isOpen,
  onClose,
  fee,
  onSuccess
}: CollectContingencyFeeModalProps) {
  const [caseOutcomeValue, setCaseOutcomeValue] = useState('')
  const [collectedValue, setCollectedValue] = useState('')
  const [accountId, setAccountId] = useState('')
  const [paymentDate, setPaymentDate] = useState('')
  const [notes, setNotes] = useState('')
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [useCustomValue, setUseCustomValue] = useState(false)
  const { formatCurrency } = useDateTimeFormat()

  useEffect(() => {
    if (isOpen) {
      loadAccounts()
      // Reset form
      setCaseOutcomeValue('')
      setCollectedValue('')
      setAccountId('')
      setPaymentDate(new Date().toISOString().split('T')[0])
      setNotes('')
      setUseCustomValue(false)
      setError('')
    }
  }, [isOpen])

  const loadAccounts = async () => {
    try {
      const response = await financeV2Service.accounts.list({ isActive: true })
      if (response.success) {
        setAccounts(response.data.accounts)
      }
    } catch (err) {
      console.error('Error loading accounts:', err)
    }
  }

  const calculateAutomaticValue = () => {
    const outcome = parseFloat(caseOutcomeValue)
    if (isNaN(outcome) || outcome <= 0) return 0
    return (outcome * fee.percentage) / 100
  }

  const automaticValue = calculateAutomaticValue()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    const outcomeNum = parseFloat(caseOutcomeValue)
    if (isNaN(outcomeNum) || outcomeNum <= 0) {
      setError('Valor obtido deve ser maior que zero')
      return
    }

    if (!accountId) {
      setError('Selecione uma conta')
      return
    }

    if (!paymentDate) {
      setError('Data de pagamento é obrigatória')
      return
    }

    const finalCollectedValue = useCustomValue ? parseFloat(collectedValue) : automaticValue

    if (isNaN(finalCollectedValue) || finalCollectedValue <= 0) {
      setError('Valor cobrado inválido')
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await financeV2Service.contingencyFees.collect(fee.id, {
        case_outcome_value: outcomeNum,
        collected_value: useCustomValue ? finalCollectedValue : undefined,
        account_id: accountId,
        payment_date: paymentDate,
        notes: notes.trim() || undefined
      })

      if (response.success) {
        onSuccess()
        onClose()
      } else {
        setError(response.message || 'Erro ao cobrar honorário')
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao cobrar honorário'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="size-4 text-muted-foreground" aria-hidden="true" />
            Cobrar Honorário Contingente
          </DialogTitle>
          <DialogDescription>Informe o valor obtido no caso para registrar o recebimento.</DialogDescription>
        </DialogHeader>

        <form id="collect-contingency-fee-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/40 p-3 sm:grid-cols-2 sm:gap-4">
            <div className="min-w-0">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Descrição</p>
              <p className="break-words text-sm">{fee.description}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Percentual</p>
              <p className="text-2xl font-semibold tabular-nums">{fee.percentage}%</p>
            </div>
          </div>

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
              <p className="text-destructive">{error}</p>
            </div>
          )}

          <FormField label="Valor Obtido no Caso *" htmlFor="caseOutcomeValue" hint="Valor total obtido na sentença ou acordo">
            <CurrencyInput
              id="caseOutcomeValue"
              value={caseOutcomeValue}
              onChange={setCaseOutcomeValue}
              required
            />
          </FormField>

          {caseOutcomeValue && !isNaN(parseFloat(caseOutcomeValue)) && (
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Calculator className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-medium">Cálculo Automático</p>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {formatCurrency(parseFloat(caseOutcomeValue))} × {fee.percentage}%
                    </p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-2xl font-semibold tabular-nums text-primary">
                    {formatCurrency(automaticValue)}
                  </p>
                  <p className="text-xs text-muted-foreground">Valor a cobrar</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3">
            <input
              id="useCustomValue"
              type="checkbox"
              checked={useCustomValue}
              onChange={(e) => setUseCustomValue(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded-sm border-input accent-primary"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <Label htmlFor="useCustomValue">Usar valor customizado</Label>
              <p className="text-xs text-muted-foreground">
                Marque para cobrar um valor diferente do cálculo automático
              </p>
            </div>
          </div>

          {useCustomValue && (
            <FormField label="Valor Cobrado (Customizado) *" htmlFor="collectedValue">
              <CurrencyInput
                id="collectedValue"
                value={collectedValue}
                onChange={setCollectedValue}
                required={useCustomValue}
              />
            </FormField>
          )}

          <FormField label="Conta para Recebimento *" htmlFor="account">
            <NativeSelect
              id="account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
            >
              <option value="">Selecione uma conta</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} - {acc.holderName}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <FormField label="Data de Pagamento *" htmlFor="paymentDate">
            <Input
              type="date"
              id="paymentDate"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              required
            />
          </FormField>

          <FormField label="Observações (Opcional)" htmlFor="notes">
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Adicione observações sobre o recebimento..."
            />
          </FormField>

          <div role="note" className="rounded-lg border bg-muted/40 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium">O que acontecerá</p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-muted-foreground">
                  <li>Uma transação do tipo &quot;Receita&quot; será criada automaticamente</li>
                  <li>O honorário será marcado como &quot;Cobrado&quot;</li>
                  <li>O valor será creditado na conta selecionada</li>
                  <li>Esta ação não pode ser desfeita</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="collect-contingency-fee-form" loading={loading}>
            {loading ? 'Cobrando...' : 'Confirmar Cobrança'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Campo numérico em reais com o prefixo "R$" dentro da caixa. */
function CurrencyInput({ id, value, onChange, required }: {
  id: string
  value: string
  onChange: (value: string) => void
  required?: boolean
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
      <Input
        type="number"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min="0"
        step="0.01"
        required={required}
        className="pl-9"
        placeholder="0,00"
      />
    </div>
  )
}
